import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  DollarSign,
  Building2,
  Calendar,
  Plus,
  Eye,
  XCircle,
} from 'lucide-react';
import { useBudget, Budget, BudgetAlert } from '@/hooks/useBudget';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import { useToast } from '@/hooks/useToast';

export default function BudgetOverviewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { getBudgetOverview, getBudgetAlerts, acknowledgeAlert } = useBudget();
  
  const { data: overview, isLoading, error, refetch } = getBudgetOverview();
  const { data: alerts } = getBudgetAlerts();
  const acknowledgeMutation = acknowledgeAlert();

  const handleAcknowledge = async (alertId: string) => {
    try {
      await acknowledgeMutation.mutateAsync(alertId);
      toast.success('Alert acknowledged', 'Budget alert has been dismissed.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to acknowledge alert';
      toast.error('Error', message);
      console.error('Failed to acknowledge alert:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Budget Overview
          </h1>
          <p className="text-text-secondary mt-1">
            Monitor and manage departmental budgets
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate('/budget-transactions')}>
            <Eye className="mr-2 h-4 w-4" />
            View Transactions
          </Button>
          <Button onClick={() => navigate('/settings/budgets')}>
            <Plus className="mr-2 h-4 w-4" />
            Manage Budgets
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading budget overview</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Budget"
          value={overview?.total_budget || 0}
          icon={Wallet}
          color="bg-blue-100 text-blue-600"
          trend="+5.2%"
          trendUp={true}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Allocated"
          value={overview?.total_allocated || 0}
          icon={TrendingUp}
          color="bg-yellow-100 text-yellow-600"
          trend="68%"
          trendUp={false}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Remaining"
          value={overview?.total_remaining || 0}
          icon={TrendingDown}
          color="bg-green-100 text-green-600"
          trend="32%"
          trendUp={true}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Active Alerts"
          value={alerts?.filter(a => !a.is_acknowledged).length || 0}
          icon={AlertTriangle}
          color="bg-red-100 text-red-600"
          isAlert
          isLoading={isLoading}
        />
      </div>

      {/* Budget Alerts */}
      {alerts && alerts.filter(a => !a.is_acknowledged).length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="h-5 w-5" />
              Budget Alerts ({alerts.filter(a => !a.is_acknowledged).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.filter(a => !a.is_acknowledged).map((alert) => (
                <div
                  key={alert.id}
                  className={cn(
                    'flex items-center justify-between p-3 rounded-lg',
                    alert.alert_type === 'critical' ? 'bg-red-100' : 'bg-yellow-100'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={cn(
                      'h-5 w-5',
                      alert.alert_type === 'critical' ? 'text-red-600' : 'text-yellow-600'
                    )} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{alert.message}</p>
                      <p className="text-xs text-gray-600">
                        Current usage: {alert.current_percentage.toFixed(1)}% of budget
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAcknowledge(alert.id)}
                    className={cn(
                      'ml-4',
                      alert.alert_type === 'critical' 
                        ? 'border-red-300 text-red-700 hover:bg-red-100' 
                        : 'border-yellow-300 text-yellow-700 hover:bg-yellow-100'
                    )}
                  >
                    Acknowledge
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Department Budgets */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Department Budgets</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-full" />
                </div>
              ))}
            </div>
          ) : overview?.departments && overview.departments.length > 0 ? (
            <div className="space-y-4">
              {overview.departments.map((budget) => (
                <DepartmentBudgetCard key={budget.id} budget={budget} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Wallet className="h-12 w-12 mx-auto text-text-muted mb-3" />
              <p className="text-text-muted">No department budgets configured</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/settings/budgets')}>
                <Plus className="mr-2 h-4 w-4" />
                Create Budget
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Budget Period Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Current Period</span>
                <Badge variant="info">Monthly</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Days Remaining</span>
                <span className="text-sm font-medium">
                  {Math.max(0, Math.ceil((new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))} days
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Utilization Rate</span>
                <span className="text-sm font-medium text-primary">
                  {overview?.total_budget ? ((overview.total_allocated / overview.total_budget) * 100).toFixed(1) : 0}%
                </span>
              </div>
              <Progress
                value={overview?.total_budget ? (overview.total_allocated / overview.total_budget) * 100 : 0}
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Budget Health Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-4">
              <div className="text-center">
                <div className={cn(
                  'text-4xl font-bold mb-2',
                  (overview?.total_budget && overview.total_allocated / overview.total_budget < 0.7)
                    ? 'text-green-600'
                    : (overview?.total_budget && overview.total_allocated / overview.total_budget < 0.9)
                    ? 'text-yellow-600'
                    : 'text-red-600'
                )}>
                  {overview?.total_budget
                    ? (overview.total_allocated / overview.total_budget < 0.7
                      ? 'Excellent'
                      : overview.total_allocated / overview.total_budget < 0.9
                      ? 'Good'
                      : 'Critical')
                    : 'N/A'
                  }
                </div>
                <p className="text-sm text-text-muted">
                  {overview?.total_budget && overview.total_allocated / overview.total_budget < 0.7
                    ? 'Budget utilization is healthy'
                    : overview?.total_budget && overview.total_allocated / overview.total_budget < 0.9
                    ? 'Approaching budget limits'
                    : overview?.total_budget
                    ? 'Immediate action required'
                    : 'No budget data available'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  icon: Icon,
  color,
  trend,
  trendUp,
  isAlert,
  isLoading,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  color: string;
  trend?: string;
  trendUp?: boolean;
  isAlert?: boolean;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm font-medium text-text-secondary">{title}</CardTitle>
        <div className={cn('p-2 rounded-md', color)}>
          <Icon className="h-5 w-5" />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <>
            <Skeleton className="h-8 w-32 mb-2" />
            <Skeleton className="h-4 w-20" />
          </>
        ) : (
          <>
            <div className={cn(
              'text-2xl font-bold',
              isAlert && value > 0 ? 'text-red-600' : 'text-text-primary'
            )}>
              {isAlert ? value : formatCurrency(value)}
            </div>
            {trend && (
              <div className={cn('flex items-center gap-1 mt-1', trendUp ? 'text-green-600' : 'text-text-muted')}>
                {trendUp !== undefined && (
                  trendUp ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />
                )}
                <span className="text-sm">{trend}</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DepartmentBudgetCard({ budget }: { budget: Budget }) {
  const utilizationRate = budget.total_budget > 0 
    ? (budget.allocated_amount / budget.total_budget) * 100 
    : 0;
  
  const isHealthy = utilizationRate < 70;
  const isWarning = utilizationRate >= 70 && utilizationRate < 90;
  const isCritical = utilizationRate >= 90;

  return (
    <div className="p-4 border border-border rounded-lg hover:bg-background-subtle transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={cn(
            'p-2 rounded-lg',
            isHealthy ? 'bg-green-100 text-green-600' : isWarning ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
          )}>
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h4 className="font-medium text-text-primary">{budget.department_name || 'Department'}</h4>
            <p className="text-xs text-text-muted">
              {format(new Date(budget.period_start), 'MMM d, yyyy')} - {format(new Date(budget.period_end), 'MMM d, yyyy')}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-text-primary">
            {formatCurrency(budget.remaining_amount)} / {formatCurrency(budget.total_budget)}
          </p>
          <Badge variant={isHealthy ? 'success' : isWarning ? 'warning' : 'error'}>
            {utilizationRate.toFixed(1)}% used
          </Badge>
        </div>
      </div>
      <Progress 
        value={Math.min(utilizationRate, 100)} 
        className={cn('h-2', isHealthy ? '' : isWarning ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500')} 
      />
    </div>
  );
}
